import * as React from 'react';
import styled from '@emotion/styled';

import { Icon } from './Icon';

export interface NumberStepperProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'type'
  > {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Lumen number stepper — Luminoso DS 2026 "Type=Number" input. A centered
 * numeric field flanked by circle-minus / circle-plus buttons. The value is
 * clamped to [min, max]; the buttons disable at the bounds.
 */
export function NumberStepper({
  value,
  onChange,
  min = -Infinity,
  max = Infinity,
  step = 1,
  disabled,
  ...rest
}: NumberStepperProps) {
  const clamp = (next: number) => Math.min(max, Math.max(min, next));

  const commit = (next: number) => {
    if (!Number.isNaN(next)) onChange(clamp(next));
  };

  return (
    <Root $disabled={disabled}>
      <StepButton
        type="button"
        onClick={() => commit(value - step)}
        disabled={disabled || value <= min}
        aria-label="Decrease"
      >
        <Icon name="circle-minus" size={16} />
      </StepButton>
      <ValueInput
        type="number"
        inputMode="numeric"
        value={Number.isFinite(value) ? value : ''}
        min={min === -Infinity ? undefined : min}
        max={max === Infinity ? undefined : max}
        step={step}
        disabled={disabled}
        onChange={event => commit(Number(event.target.value))}
        {...rest}
      />
      <StepButton
        type="button"
        onClick={() => commit(value + step)}
        disabled={disabled || value >= max}
        aria-label="Increase"
      >
        <Icon name="circle-plus" size={16} />
      </StepButton>
    </Root>
  );
}

/* Matches the other lumen controls (Figma 513-7272): one bordered pill,
   height/border identical to Select/Input via the controlBase tokens. */
const Root = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: ${({ theme }) => theme.control.height.sm};
  padding: 0 10px;
  background: ${({ theme }) => theme.color.bg.default};
  border: 1px solid ${({ theme }) => theme.color.slate[500]};
  border-radius: ${({ theme }) => theme.radius.base};
  transition: border-color ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.ease},
    box-shadow ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.ease};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${({ theme }) => theme.color.blue[400]};
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.color.primary.base};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.blue[100]};
  }
`;

const StepButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.color.fg.muted};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.color.fg.default};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ValueInput = styled.input`
  /* "&&" doubles specificity so legacy global input[type] styles can't
     re-add their own border/box around the value */
  && {
    flex: 1 0 0;
    min-width: 0;
    height: 100%;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: none;
    box-shadow: none;
    outline: none;
  }

  text-align: center;
  font-family: inherit;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.color.fg.default};

  /* Hide the native spinner — the ± buttons replace it. */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;
